import { MDBIcon } from "mdbreact";
import {
  autoSelect,
  autoVariant,
  brandsOf,
  isComplete,
} from "../../brandedProducts/select";
import { newBrandsOf } from "./status";
import { packLabel } from "../../products/common";

/**
 * Tagged generic of a service with its default brand/variant and units,
 * e.g. SYR – Disposable Syringe | Terumo ★ · 3cc ★ | − 1 +
 */
export default function SupplyRow({
  item,
  products,
  taggedAt,
  onChange,
  onRemove,
  disabled,
}) {
  const { generic, product, variant, quantity } = item;
  const brands = brandsOf(products, generic.id);
  const variants = product?.variants || [];
  const complete = isComplete(item);
  // brands added since the last save of this tagging
  const newBrands = newBrandsOf(products, item, taggedAt);
  // tagged without a brand, but one can be selected now
  const canSelect = !product && brands.length > 0;
  const preset = canSelect ? autoSelect(products, generic.id) : null;

  const setUnits = (value) =>
    onChange({ quantity: Math.max(Number(value) || 1, 1) });

  return (
    <tr>
      <td className="align-middle">
        <code className="mr-1">{generic.abbreviation}</code>
        <b>{generic.name}</b>
        <small className="d-block text-muted">
          {generic.section} · uom: {generic.uom}
        </small>
      </td>

      <td className="align-middle">
        {brands.length ? (
          <>
            <div className="d-flex" style={{ gap: 4 }}>
              <select
                className={`browser-default custom-select custom-select-sm ${
                  product ? "" : "is-invalid"
                }`}
                value={product?._id || ""}
                onChange={({ target }) => {
                  const next =
                    brands.find(({ _id }) => _id === target.value) || null;
                  onChange({ product: next, variant: autoVariant(next) });
                }}
                disabled={disabled}
              >
                <option value="">Select brand</option>
                {brands.map(({ _id, name, isDefault }) => (
                  <option key={_id} value={_id}>
                    {name}
                    {isDefault ? " ★" : ""}
                  </option>
                ))}
              </select>
              {variants.length > 0 && (
                <select
                  className={`browser-default custom-select custom-select-sm ${
                    variant ? "" : "is-invalid"
                  }`}
                  value={variant?._id || ""}
                  onChange={({ target }) =>
                    onChange({
                      variant:
                        variants.find(({ _id }) => _id === target.value) ||
                        null,
                    })
                  }
                  disabled={disabled}
                >
                  <option value="">Variant</option>
                  {variants.map(({ _id, name, isDefault }) => (
                    <option key={_id} value={_id}>
                      {name}
                      {isDefault ? " ★" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {complete && packLabel(variant, product) && (
              <small className="text-muted d-block">
                {packLabel(variant, product)}
              </small>
            )}
            {canSelect && (
              <small className="text-primary d-block">
                <MDBIcon icon="bell" className="mr-1" />
                {brands.length} brand{brands.length > 1 ? "s" : ""} available
                {preset?.product && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 m-0 ml-1 text-primary"
                    style={{ textTransform: "none", verticalAlign: "baseline" }}
                    onClick={() => onChange(preset)}
                    disabled={disabled}
                  >
                    Use {preset.product.name}
                    {preset.variant ? ` · ${preset.variant.name}` : ""}
                  </button>
                )}
              </small>
            )}
            {newBrands.length > 0 && (
              <small
                className="text-primary d-block"
                title={newBrands.map(({ name }) => name).join(", ")}
              >
                <MDBIcon icon="bell" className="mr-1" />+{newBrands.length} new
                brand
                {newBrands.length > 1 ? "s" : ""}:{" "}
                {newBrands.map(({ name }) => name).join(", ")}
              </small>
            )}
          </>
        ) : (
          <small className="text-danger">
            <MDBIcon icon="ban" className="mr-1" />
            No brand yet. Tagged; add one in Branded Products to auto-consume.
          </small>
        )}
      </td>

      <td className="align-middle">
        <div className="d-flex align-items-center" style={{ gap: 2 }}>
          <button
            type="button"
            className="btn btn-sm btn-light m-0 px-2 py-1 z-depth-0"
            onClick={() => setUnits(quantity - 1)}
            disabled={disabled || quantity <= 1}
            title="Decrease"
          >
            <MDBIcon icon="minus" />
          </button>
          <input
            type="number"
            min={1}
            className="form-control form-control-sm text-center"
            style={{ width: 52 }}
            value={quantity}
            onChange={({ target }) => setUnits(target.value)}
            disabled={disabled}
          />
          <button
            type="button"
            className="btn btn-sm btn-light m-0 px-2 py-1 z-depth-0"
            onClick={() => setUnits(quantity + 1)}
            disabled={disabled}
            title="Increase"
          >
            <MDBIcon icon="plus" />
          </button>
        </div>
      </td>

      <td className="align-middle text-center">
        <MDBIcon
          icon={
            complete
              ? "check-circle"
              : canSelect
                ? "bell"
                : "exclamation-triangle"
          }
          style={{
            color: complete ? "#00a65a" : canSelect ? "#2196f3" : "#f39c12",
          }}
          title={
            complete
              ? "Auto-consume ready"
              : canSelect
                ? "A brand is available, select the default"
                : "No brand yet"
          }
        />
      </td>

      <td className="align-middle text-center">
        <MDBIcon
          icon="trash"
          className="text-danger"
          style={{ cursor: disabled ? "default" : "pointer" }}
          onClick={() => !disabled && onRemove()}
          title="Remove"
        />
      </td>
    </tr>
  );
}
